/**
 * Blind count entry — `/inventory/counts/$worksheetId`.
 *
 * BLIND IS BINDING. This screen requests the `entry` representation, whose
 * line schema simply has no snapshot or variance member: the expected
 * quantity is absent from the wire, not hidden in the client. Nothing here
 * reads, derives or displays it.
 *
 * Corrections are re-counts: typing a new quantity and saving replaces the
 * counter's own figure through the same audited endpoints. Entry batches
 * carry an Idempotency-Key and the worksheet ETag, so a double-tap on a bad
 * connection cannot double-post.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Ban, Check, Save, Send } from "lucide-react";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, toast } from "@erp/ui";

import { ConflictDialog, surfaceProblem } from "@/components/form";
import { isPreconditionFailed, newIdempotencyKey } from "@/features/shared/ops-api";

import {
  QUANTITY_FRACTION_DIGITS,
  cancelCountWorksheet,
  recordCountEntries,
  recordCountLine,
  startCountWorksheet,
  submitCountWorksheet,
  type CountLineBlind,
} from "../api";
import { isNegativeDecimal, toScaledDecimalString } from "../decimal";
import { isCountIncomplete, isInvalidTransition } from "../problems";
import { inventoryKeys, useCountEntry, useWarehouseOptions } from "../queries";
import { QuantityText } from "../components/decimal-text";
import { ScanBox } from "../components/scan-box";

const COUNT_BADGE_VARIANTS = {
  draft: "outline",
  counting: "warning",
  submitted: "default",
  approved: "success",
  cancelled: "secondary",
} as const;

export interface CountEntryPageProps {
  worksheetId?: string;
  canEnter?: boolean;
  canSubmit?: boolean;
  canCancel?: boolean;
  canReview?: boolean;
  canScan?: boolean;
  onOpenReview?: (worksheetId: string) => void;
  onBack?: () => void;
}

export function CountEntryPage({
  worksheetId,
  canEnter = false,
  canSubmit = false,
  canCancel = false,
  canReview = false,
  canScan = false,
  onOpenReview,
  onBack,
}: CountEntryPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [lineErrors, setLineErrors] = useState<Record<string, string>>({});
  const [scanMiss, setScanMiss] = useState<string | null>(null);
  const [focusLineId, setFocusLineId] = useState<string | null>(null);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  const detail = useCountEntry(worksheetId);
  const worksheet = detail.data?.data ?? null;
  const etag = detail.data?.etag ?? null;
  const warehouses = useWarehouseOptions().data ?? [];

  const lines = useMemo<readonly CountLineBlind[]>(() => worksheet?.lines ?? [], [worksheet]);
  const countedLines = useMemo(
    () => lines.filter((line) => line.counted_qty !== undefined).length,
    [lines],
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.counts.all });
  };

  const explainFailure = (error: unknown): boolean => {
    if (isPreconditionFailed(error)) {
      setConflict(true);
      return true;
    }
    if (isInvalidTransition(error)) {
      setTransitionError(
        t(
          "inventory.countEntry.invalidTransition",
          "This worksheet is {{status}} elsewhere — reload before counting further.",
          {
            status: t(
              `inventory.countStatus.${worksheet?.status ?? "draft"}`,
              worksheet?.status ?? "",
            ),
          },
        ),
      );
      return true;
    }
    if (isCountIncomplete(error)) {
      setTransitionError(
        t(
          "inventory.countEntry.incomplete",
          "Every line needs a counted quantity before this worksheet can be submitted.",
        ),
      );
      return true;
    }
    return false;
  };

  const startMutation = useMutation({
    mutationFn: () => {
      if (worksheetId === undefined || etag === null) throw new Error("Worksheet is not loaded");
      return startCountWorksheet(worksheetId, etag, newIdempotencyKey());
    },
    onSuccess: () => {
      setTransitionError(null);
      invalidate();
    },
    onError: (error) => {
      if (explainFailure(error)) return;
      surfaceProblem(error, t);
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => {
      if (worksheetId === undefined || etag === null) throw new Error("Worksheet is not loaded");
      return submitCountWorksheet(worksheetId, etag, newIdempotencyKey());
    },
    onSuccess: () => {
      setTransitionError(null);
      toast({
        variant: "success",
        title: t("inventory.countEntry.submitted", "Worksheet submitted for review"),
      });
      invalidate();
    },
    onError: (error) => {
      if (explainFailure(error)) return;
      surfaceProblem(error, t);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => {
      if (worksheetId === undefined || etag === null) throw new Error("Worksheet is not loaded");
      return cancelCountWorksheet(worksheetId, etag, newIdempotencyKey());
    },
    onSuccess: () => {
      setTransitionError(null);
      toast({
        variant: "success",
        title: t("inventory.countEntry.cancelled", "Worksheet cancelled"),
      });
      invalidate();
    },
    onError: (error) => {
      if (explainFailure(error)) return;
      surfaceProblem(error, t);
    },
  });

  const batchMutation = useMutation({
    mutationFn: (entries: { count_line_id: string; counted_qty: string }[]) => {
      if (worksheetId === undefined || etag === null) throw new Error("Worksheet is not loaded");
      return recordCountEntries(worksheetId, { entries }, etag, newIdempotencyKey());
    },
    onSuccess: () => {
      setDrafts({});
      setLineErrors({});
      toast({ variant: "success", title: t("inventory.countEntry.saved", "Counts saved") });
      invalidate();
    },
    onError: (error) => {
      if (explainFailure(error)) return;
      surfaceProblem(error, t);
    },
  });

  const lineMutation = useMutation({
    mutationFn: (input: { lineId: string; countedQty: string }) => {
      if (worksheetId === undefined || etag === null) throw new Error("Worksheet is not loaded");
      return recordCountLine(worksheetId, input.lineId, { counted_qty: input.countedQty }, etag);
    },
    onSuccess: (_result, input) => {
      setDrafts((current) => {
        const next = { ...current };
        delete next[input.lineId];
        return next;
      });
      invalidate();
    },
    onError: (error) => {
      if (explainFailure(error)) return;
      surfaceProblem(error, t);
    },
  });

  const scaleDraft = (lineId: string, raw: string): string | null => {
    const scaled = toScaledDecimalString(raw, QUANTITY_FRACTION_DIGITS);
    if (scaled === null || isNegativeDecimal(scaled)) {
      setLineErrors((current) => ({
        ...current,
        [lineId]: t(
          "inventory.countEntry.qtyInvalid",
          "Enter zero or more, with at most 6 decimal places.",
        ),
      }));
      return null;
    }
    setLineErrors((current) => {
      const next = { ...current };
      delete next[lineId];
      return next;
    });
    return scaled;
  };

  const saveAll = () => {
    const entries: { count_line_id: string; counted_qty: string }[] = [];
    for (const [lineId, raw] of Object.entries(drafts)) {
      if (raw.trim() === "") continue;
      const scaled = scaleDraft(lineId, raw);
      if (scaled === null) return;
      entries.push({ count_line_id: lineId, counted_qty: scaled });
    }
    if (entries.length === 0) return;
    batchMutation.mutate(entries);
  };

  if (detail.isError) {
    return (
      <div className="p-4">
        <p role="alert" className="text-sm text-destructive">
          {t("inventory.countEntry.loadError", "This worksheet could not be loaded.")}
        </p>
      </div>
    );
  }

  if (worksheet === null) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">{t("inventory.common.loading", "Loading…")}</p>
      </div>
    );
  }

  const warehouse = warehouses.find((candidate) => candidate.id === worksheet.warehouse_id);
  const isCounting = worksheet.status === "counting";
  const isDraft = worksheet.status === "draft";
  const dirtyCount = Object.values(drafts).filter((value) => value.trim() !== "").length;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold">
            <span dir="ltr" className="font-mono text-base">
              {worksheet.count_number}
            </span>
            <Badge variant={COUNT_BADGE_VARIANTS[worksheet.status]}>
              {t(`inventory.countStatus.${worksheet.status}`, worksheet.status)}
            </Badge>
          </h1>
          <p className="text-sm text-muted-foreground">
            {warehouse?.code ?? worksheet.warehouse_id} ·{" "}
            {t("inventory.countEntry.progress", "{{counted}} of {{total}} lines counted", {
              counted: countedLines,
              total: lines.length,
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canReview && onOpenReview !== undefined && worksheet.status !== "draft" ? (
            <Button type="button" variant="outline" onClick={() => onOpenReview(worksheet.id)}>
              {t("inventory.countEntry.review", "Variance review")}
            </Button>
          ) : null}
          {onBack !== undefined ? (
            <Button type="button" variant="ghost" onClick={onBack}>
              {t("inventory.countEntry.back", "All worksheets")}
            </Button>
          ) : null}
        </div>
      </div>

      <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
        {t(
          "inventory.countEntry.blindNotice",
          "Blind count: the expected quantity is not part of this screen's data at all. Count what is on the shelf.",
        )}
      </p>

      {transitionError !== null ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
        >
          {transitionError}
        </p>
      ) : null}

      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>
            <h2>{t("inventory.countEntry.lines", "Lines")}</h2>
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            {isDraft && canEnter ? (
              <Button
                type="button"
                loading={startMutation.isPending}
                disabled={etag === null || startMutation.isPending}
                onClick={() => startMutation.mutate()}
              >
                {t("inventory.countEntry.start", "Start counting")}
              </Button>
            ) : null}
            {isCounting && canEnter ? (
              <Button
                type="button"
                variant="outline"
                loading={batchMutation.isPending}
                disabled={dirtyCount === 0 || etag === null || batchMutation.isPending}
                onClick={saveAll}
              >
                <Save className="size-4" aria-hidden="true" />
                {t("inventory.countEntry.saveAll", "Save {{count}} counts", { count: dirtyCount })}
              </Button>
            ) : null}
            {isCounting && canSubmit ? (
              <Button
                type="button"
                loading={submitMutation.isPending}
                disabled={etag === null || submitMutation.isPending}
                onClick={() => submitMutation.mutate()}
              >
                <Send className="size-4 rtl:rotate-180" aria-hidden="true" />
                {t("inventory.countEntry.submit", "Submit for review")}
              </Button>
            ) : null}
            {(isDraft || isCounting) && canCancel ? (
              <Button
                type="button"
                variant="ghost"
                loading={cancelMutation.isPending}
                disabled={etag === null || cancelMutation.isPending}
                onClick={() => cancelMutation.mutate()}
              >
                <Ban className="size-4" aria-hidden="true" />
                {t("inventory.countEntry.cancel", "Cancel worksheet")}
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {isCounting ? (
            <ScanBox
              id="count-scan"
              autoFocus
              canResolve={canScan}
              label={t("inventory.countEntry.scan", "Scan an item")}
              onResolved={(variant) => {
                const match = lines.find((line) => line.variant_id === variant.id);
                if (match === undefined) {
                  setScanMiss(variant.sku);
                  return;
                }
                setScanMiss(null);
                setFocusLineId(match.id);
                document.getElementById(`count-line-${match.id}`)?.focus();
              }}
            />
          ) : null}

          {scanMiss !== null ? (
            <p
              role="alert"
              className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm font-medium text-destructive"
            >
              {t("inventory.countEntry.notOnSheet", "{{sku}} is not on this worksheet.", {
                sku: scanMiss,
              })}
            </p>
          ) : null}

          {lines.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("inventory.countEntry.noLines", "This worksheet has no lines.")}
            </p>
          ) : (
            <ul className="flex flex-col divide-y">
              {lines.map((line) => {
                const draft = drafts[line.id];
                const value = draft ?? line.counted_qty ?? "";
                return (
                  <li
                    key={line.id}
                    className={
                      focusLineId === line.id
                        ? "flex flex-wrap items-center gap-3 rounded-md bg-primary/5 py-3"
                        : "flex flex-wrap items-center gap-3 py-3"
                    }
                  >
                    <span className="w-28 shrink-0 font-mono text-xs" dir="ltr">
                      {line.location_code}
                    </span>
                    <span className="min-w-0 flex-1 font-mono text-sm" dir="ltr">
                      {line.sku}
                    </span>
                    {line.batch_no !== undefined ? (
                      <Badge variant="secondary">{line.batch_no}</Badge>
                    ) : null}
                    {isCounting && canEnter ? (
                      <>
                        <Input
                          id={`count-line-${line.id}`}
                          dir="ltr"
                          inputMode="decimal"
                          className="w-32 text-end"
                          aria-label={t("inventory.countEntry.countedFor", "Counted for {{sku}}", {
                            sku: line.sku,
                          })}
                          aria-invalid={lineErrors[line.id] !== undefined}
                          value={value}
                          onChange={(event) =>
                            setDrafts((current) => ({ ...current, [line.id]: event.target.value }))
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t("inventory.countEntry.saveLine", "Save {{sku}}", {
                            sku: line.sku,
                          })}
                          disabled={
                            draft === undefined || draft.trim() === "" || lineMutation.isPending
                          }
                          onClick={() => {
                            const scaled = scaleDraft(line.id, draft ?? "");
                            if (scaled === null) return;
                            lineMutation.mutate({ lineId: line.id, countedQty: scaled });
                          }}
                        >
                          <Check className="size-4" aria-hidden="true" />
                        </Button>
                      </>
                    ) : (
                      <span className="w-32 text-end">
                        {line.counted_qty === undefined ? (
                          <span className="text-muted-foreground">
                            {t("inventory.countEntry.uncounted", "not counted")}
                          </span>
                        ) : (
                          <QuantityText value={line.counted_qty} />
                        )}
                      </span>
                    )}
                    {lineErrors[line.id] !== undefined ? (
                      <p role="alert" className="w-full text-sm text-destructive">
                        {lineErrors[line.id]}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <ConflictDialog
        open={conflict}
        onReload={() => {
          setConflict(false);
          setDrafts({});
          void detail.refetch();
        }}
        onDismiss={() => setConflict(false)}
      />
    </div>
  );
}
